import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { PHASE_OF_PLAY_META } from '@4cm/core/phaseOfPlay';
import { getFeatures } from '@4cm/core/features';
import { Brand } from '../components/Brand';
import { BrainOrb, BrainPrompt, Button, GlassPill } from '../components/primitives';
import { VoicePicker } from '../components/VoicePicker';
import { TeamSetupForm } from '../components/TeamSetupForm';
import { useAuth } from '../contexts/AuthContext';
import { useCalibration } from '../contexts/CalibrationContext';
import { useVoiceToText } from '../lib/voiceInput';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { useVoicePrefs } from '../lib/queries/useVoicePrefs';
import {
  applySeededTestRoster,
  buildOnboardingSessions,
  completedStageCount,
  composeSaveSuccessReply,
  createInitialOnboardingProfile,
  derivePhilosophyAndCurriculum,
  detectEditIntent,
  extractOnboardingInput,
  fillStageDefaults,
  isImplicitPhilosophyCoach,
  isOnboardingConfirmation,
  isSkipRequest,
  isStageComplete,
  nextStage,
  ONBOARDING_STAGE_ORDER,
  saveOnboardingPlan,
  softCaptureForStage,
  STAGE_META,
  stageAcknowledgement,
  stageQuestion,
} from '../lib/onboarding';
import type { OnboardingProfile, OnboardingStage } from '../lib/onboarding';
import { saveLocalTeam } from '../lib/localTeam';
import { clearOnboardingDeferred, markOnboardingDeferred } from '../lib/onboardingStatus';
import './Onboarding.css';

interface ConversationTurn {
  id: string;
  role: 'coach' | 'brain';
  text: string;
  /** Chips shown above the Brain reply text — recognition feedback. */
  heard?: HeardChip[];
}

function firstTurnForCoach(name: string | null | undefined): ConversationTurn {
  return {
    id: 'first',
    role: 'brain',
    text: stageQuestion('coach', createInitialOnboardingProfile(), name ?? null),
  };
}

/** Map from stage → diffProfileForChips field keys that should highlight
 *  that stage card when the coach answers. */
const STAGE_FLASH_KEYS: Record<OnboardingStage, string[]> = {
  coach:       ['years', 'badges', 'played', 'admires', 'supports', 'enjoys', 'gripes'],
  team:        ['team', 'age', 'squad', 'tier', 'league', 'formation'],
  roster:      ['roster'],
  season:      ['season'],
  aims:        ['aim', 'focus', 'principles'],
  schedule:    ['nights', 'start', 'duration'],
  plan:        ['weeks'],
  philosophy:  ['philosophy'],
  curriculum:  ['curriculum'],
  confirm:     [],
  walkthrough: [],
  done:        [],
};

function StageBody({
  stage,
  profile,
  phaseLabel,
}: {
  stage: OnboardingStage;
  profile: OnboardingProfile;
  phaseLabel: string;
}): ReactElement | null {
  switch (stage) {
    case 'coach':
      return (
        <dl className="onboarding__stage-facts">
          <Pair k="Years coaching" v={profile.yearsCoaching != null ? `${profile.yearsCoaching} yr` : null} />
          <Pair k="Badges" v={profile.badges} />
          <Pair k="Played" v={profile.playedTo} />
          <Pair k="Admires" v={profile.admiredCoaches} />
          <Pair k="Supports" v={profile.supportsTeam} />
          <Pair k="Loves" v={profile.enjoys} />
          <Pair k="Finds hard" v={profile.frustrations} />
        </dl>
      );
    case 'team':
      return (
        <dl className="onboarding__stage-facts">
          <Pair k="Team" v={profile.teamName} />
          <Pair
            k="Age · Squad"
            v={
              profile.ageGroup && profile.playerCount
                ? `${profile.ageGroup} · ${profile.playerCount} players`
                : profile.ageGroup ?? null
            }
          />
          <Pair k="Level" v={profile.coachLevel ? profile.coachLevel.toLowerCase().replace('_', ' ') : null} />
          <Pair k="League" v={profile.leagueName} />
          <Pair k="Formation" v={profile.formation} />
        </dl>
      );
    case 'roster': {
      const n = profile.roster?.length ?? 0;
      const placeholder = profile.roster?.[0]?.placeholder ?? false;
      return (
        <dl className="onboarding__stage-facts">
          <Pair
            k="Players"
            v={
              profile.roster == null
                ? null
                : n === 0
                  ? 'skipped — seed pending'
                  : `${n}${placeholder ? ' placeholder' : ''}${n === 1 ? '' : placeholder ? 's' : ''}`
            }
          />
        </dl>
      );
    }
    case 'season':
      return (
        <dl className="onboarding__stage-facts">
          <Pair k="Phase" v={profile.seasonPhase ? profile.seasonPhase.replace('_', ' ') : null} />
          <Pair k="Note" v={profile.seasonNote} />
        </dl>
      );
    case 'aims':
      return (
        <dl className="onboarding__stage-facts">
          <Pair k="Methodology" v={profile.methodology} />
          {profile.methodologyPrinciples && profile.methodologyPrinciples.length > 0 && (
            <Pair
              k="Principles"
              v={profile.methodologyPrinciples.join(' · ')}
            />
          )}
          {/* Focus is only meaningful once Brain has prescribed it
              (focusRationale set) — otherwise it's just the DEFAULT_PROFILE
              seed and would surface as "Attacking out of nowhere". */}
          <Pair
            k="Focus"
            v={profile.focusRationale && profile.focus ? phaseLabel : null}
          />
          {profile.methodologyRationale && (
            <Pair k="Why" v={profile.methodologyRationale} />
          )}
        </dl>
      );
    case 'schedule': {
      const days = profile.trainingDays && profile.trainingDays.length > 0
        ? profile.trainingDays.join(' / ')
        : null;
      return (
        <dl className="onboarding__stage-facts">
          <Pair k="Nights" v={days} />
          <Pair k="Start" v={profile.trainingStartTime} />
          <Pair k="Session length" v={`${profile.sessionDurationMins} min`} />
        </dl>
      );
    }
    case 'plan':
      return (
        <dl className="onboarding__stage-facts">
          <Pair k="Weeks" v={profile.weeksToPlan != null ? `${profile.weeksToPlan}` : null} />
        </dl>
      );
    case 'philosophy':
      if (!profile.philosophy) {
        return <p className="onboarding__stage-empty">Drafting principles…</p>;
      }
      return (
        <div className="onboarding__philosophy">
          {(['attack', 'defend', 'transition', 'restart'] as const).map((phase) => (
            <div key={phase} className="onboarding__phase">
              <span className="onboarding__phase-tag">{phase}</span>
              <ul className="onboarding__principle-list">
                {profile.philosophy![phase].map((pr, i) => (
                  <li key={i} className="onboarding__principle">
                    <span className="onboarding__principle-text">{pr.text}</span>
                    <span className="onboarding__principle-tags">
                      {pr.cornerTags.map((t) => (
                        <span key={t} className="onboarding__corner-tag" data-corner={t}>
                          {cornerLabel(t)}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    case 'curriculum': {
      if (!profile.curriculum) {
        return <p className="onboarding__stage-empty">Drafting the mesocycle…</p>;
      }
      const meso = profile.curriculum.mesocycle;
      return (
        <div className="onboarding__mesocycle">
          <header className="onboarding__mesocycle-head">
            <span className="onboarding__mesocycle-tag">
              {meso.weekCount}-week {meso.phaseEmphasis} mesocycle
            </span>
            <h3 className="onboarding__mesocycle-headline">{meso.headline}</h3>
            <p className="onboarding__mesocycle-rationale">
              One subject across the whole block — variety comes from week-by-week sub-themes, not theme-flipping.
            </p>
          </header>
          <ol className="onboarding__curriculum">
            {meso.weeks.map((w) => (
              <li key={w.week} className="onboarding__curriculum-week">
                <span className="onboarding__curriculum-num">Wk {w.week}</span>
                <div className="onboarding__curriculum-detail">
                  <span className="onboarding__curriculum-theme">{w.subTheme}</span>
                  {w.constraints.length > 0 && (
                    <ul className="onboarding__curriculum-constraints">
                      {w.constraints.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      );
    }
    case 'confirm':
      return (
        <p className="onboarding__stage-confirm">
          Say <strong>yes</strong> in chat to confirm, or tell me what to change.
        </p>
      );
    case 'walkthrough':
    case 'done':
      return null;
  }
}

function Pair({ k, v }: { k: string; v: string | number | null }): ReactElement {
  const empty = v == null || v === '';
  return (
    <div>
      <dt>{k}</dt>
      <dd data-empty={empty || undefined}>{empty ? '—' : String(v)}</dd>
    </div>
  );
}

function cornerLabel(t: string): string {
  switch (t) {
    case 'corner-technical': return 'tech';
    case 'corner-physical':  return 'phys';
    case 'corner-psych':     return 'psy';
    case 'corner-social':    return 'soc';
    default: return t;
  }
}

interface HeardChip {
  field: string;
  value: string;
}

/** Diff two onboarding profiles and return human-readable chips for the
 *  fields that changed. Powers the "Heard:" feedback strip — gives the
 *  coach immediate evidence that Brain parsed what they said. */
function diffProfileForChips(
  prev: OnboardingProfile,
  next: OnboardingProfile,
): HeardChip[] {
  const chips: HeardChip[] = [];
  if (prev.teamName !== next.teamName && next.teamName) {
    chips.push({ field: 'team', value: String(next.teamName) });
  }
  if (prev.ageGroup !== next.ageGroup && next.ageGroup) {
    chips.push({ field: 'age', value: String(next.ageGroup) });
  }
  if (prev.playerCount !== next.playerCount && next.playerCount) {
    chips.push({ field: 'squad', value: `${next.playerCount} players` });
  }
  if (prev.coachLevel !== next.coachLevel && next.coachLevel) {
    chips.push({ field: 'tier', value: next.coachLevel.toLowerCase().replace('_', ' ') });
  }
  // Focus is only meaningful once Brain has *prescribed* it (focusRationale
  // set). The 'attacking' DEFAULT_PROFILE seed must not surface as a chip —
  // that's what made it look like Brain was inventing focus out of nowhere.
  if (
    prev.focus !== next.focus
    && next.focus
    && next.focusRationale !== null
  ) {
    chips.push({ field: 'focus', value: next.focus });
  } else if (
    prev.focusRationale === null
    && next.focusRationale !== null
    && next.focus
  ) {
    // First prescription — show the focus chip even if the value didn't change.
    chips.push({ field: 'focus', value: next.focus });
  }
  if (prev.methodology !== next.methodology && next.methodology) {
    const m = String(next.methodology);
    chips.push({ field: 'aim', value: m.length > 48 ? m.slice(0, 46) + '…' : m });
  }
  if (
    (prev.methodologyPrinciples ?? []).length !== (next.methodologyPrinciples ?? []).length
    && next.methodologyPrinciples
    && next.methodologyPrinciples.length > 0
  ) {
    chips.push({
      field: 'principles',
      value: next.methodologyPrinciples.length === 1
        ? '1 principle'
        : `${next.methodologyPrinciples.length} principles`,
    });
  }
  if (prev.formation !== next.formation && next.formation) {
    chips.push({ field: 'formation', value: String(next.formation) });
  }
  if (
    (prev.trainingDays?.join(',') ?? '') !== (next.trainingDays?.join(',') ?? '')
    && next.trainingDays
    && next.trainingDays.length > 0
  ) {
    chips.push({ field: 'nights', value: next.trainingDays.join('/') });
  }
  if (prev.trainingStartTime !== next.trainingStartTime && next.trainingStartTime) {
    chips.push({ field: 'start', value: next.trainingStartTime });
  }
  if (
    prev.sessionDurationMins !== next.sessionDurationMins
    && next.sessionDurationMins
    && (prev.trainingDays !== null || next.trainingDays !== null)
  ) {
    chips.push({ field: 'duration', value: `${next.sessionDurationMins} min` });
  }
  if (prev.yearsCoaching !== next.yearsCoaching && next.yearsCoaching != null) {
    chips.push({ field: 'years', value: `${next.yearsCoaching} yr` });
  }
  if (prev.badges !== next.badges && next.badges) {
    chips.push({ field: 'badges', value: next.badges });
  }
  if (prev.playedTo !== next.playedTo && next.playedTo) {
    chips.push({ field: 'played', value: next.playedTo });
  }
  if (prev.admiredCoaches !== next.admiredCoaches && next.admiredCoaches) {
    const v = String(next.admiredCoaches);
    chips.push({ field: 'admires', value: v.length > 40 ? v.slice(0, 38) + '…' : v });
  }
  if (prev.supportsTeam !== next.supportsTeam && next.supportsTeam) {
    chips.push({ field: 'supports', value: next.supportsTeam });
  }
  if (prev.enjoys !== next.enjoys && next.enjoys) {
    const v = String(next.enjoys);
    chips.push({ field: 'enjoys', value: v.length > 40 ? v.slice(0, 38) + '…' : v });
  }
  if (prev.frustrations !== next.frustrations && next.frustrations) {
    const v = String(next.frustrations);
    chips.push({ field: 'gripes', value: v.length > 40 ? v.slice(0, 38) + '…' : v });
  }
  if (prev.leagueName !== next.leagueName && next.leagueName) {
    chips.push({ field: 'league', value: next.leagueName });
  }
  if (prev.seasonPhase !== next.seasonPhase && next.seasonPhase) {
    chips.push({ field: 'season', value: next.seasonPhase.replace('_', ' ') });
  }
  if (prev.weeksToPlan !== next.weeksToPlan && next.weeksToPlan != null) {
    chips.push({ field: 'weeks', value: `${next.weeksToPlan} wk` });
  }
  if (prev.roster !== next.roster && next.roster) {
    const n = next.roster.length;
    if (n === 0) {
      chips.push({ field: 'roster', value: 'skipped — will seed' });
    } else if (next.roster[0]?.placeholder) {
      chips.push({ field: 'roster', value: `${n} placeholders` });
    } else {
      chips.push({ field: 'roster', value: `${n} players` });
    }
  }
  return chips;
}

interface WalkthroughPanelProps {
  sessions: ReturnType<typeof buildOnboardingSessions>;
  index: number;
  onNext: () => void;
  onOpenCalendar: () => void;
}

/** The walkthrough panel — replaces the "calendar populated, navigating in
 *  3s" cut. Brain narrates each Week-1 session in turn via TTS; the coach
 *  can advance, jump back into the calendar, or just talk to Brain. */
function WalkthroughPanel({ sessions, index, onNext, onOpenCalendar }: WalkthroughPanelProps) {
  if (sessions.length === 0) {
    return (
      <div className="onboarding__done-banner" role="status">
        <span className="onboarding__done-eyebrow">Setup complete</span>
        <p>Your team's saved. Have a look at the calendar.</p>
        <Button variant="primary" size="touchline" onClick={onOpenCalendar}>
          Open calendar
        </Button>
      </div>
    );
  }
  const safeIndex = Math.min(index, sessions.length - 1);
  const session = sessions[safeIndex];
  const finished = index >= sessions.length;
  return (
    <div className="onboarding__walkthrough" role="region" aria-label="Week 1 walkthrough">
      <header className="onboarding__walkthrough-head">
        <span className="onboarding__eyebrow">Walkthrough</span>
        <span className="onboarding__walkthrough-count">
          {finished ? `${sessions.length}/${sessions.length}` : `${safeIndex + 1}/${sessions.length}`}
        </span>
      </header>
      {!finished && session && (
        <article className="onboarding__walkthrough-session">
          <time>{session.date}</time>
          <h3>{session.title}</h3>
          {session.objectives.length > 0 && (
            <ul className="onboarding__walkthrough-obj">
              {session.objectives.slice(0, 3).map((obj, i) => (
                <li key={i}>{obj}</li>
              ))}
            </ul>
          )}
          {session.psychSocialIntent && (
            <p className="onboarding__walkthrough-psy">
              <strong>Mental / social intent:</strong> {session.psychSocialIntent}
            </p>
          )}
        </article>
      )}
      {finished && (
        <div className="onboarding__walkthrough-done">
          <p>That's Week 1. The calendar's ready when you are.</p>
        </div>
      )}
      <div className="onboarding__walkthrough-actions">
        {!finished && safeIndex < sessions.length - 1 && (
          <Button variant="glass" size="workbench" onClick={onNext}>
            Next session
          </Button>
        )}
        <Button variant="primary" size="touchline" onClick={onOpenCalendar}>
          {finished ? 'Open calendar' : 'I\'ll have a look myself'}
        </Button>
      </div>
    </div>
  );
}

export function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setCoachLevel } = useCalibration();
  // Coach first name from the auth profile — used to personalise Brain's
  // opening question. Auth bypass users have a name set in the fixture.
  const coachFirstName = useMemo(() => {
    const raw = (user?.user_metadata?.name as string | undefined) ?? user?.email ?? null;
    if (!raw) return null;
    return raw.split(/[\s.@_-]+/)[0] || null;
  }, [user]);
  const [profile, setProfile] = useState<OnboardingProfile>(() => createInitialOnboardingProfile());
  // Structured edit panel toggle — the FA-driven dropdown form is always
  // shown at 'confirm'; before that the coach can open it to correct fields.
  const [editOpen, setEditOpen] = useState(false);
  const [stage, setStage] = useState<OnboardingStage>('coach');
  const [turns, setTurns] = useState<ConversationTurn[]>(() => [firstTurnForCoach(coachFirstName)]);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  /** Index of the current session Brain is narrating in the walkthrough
   *  stage. Advances on "next" intent or the Next button. */
  const [walkthroughIndex, setWalkthroughIndex] = useState(0);

  const stagesDone = completedStageCount(profile);
  const phaseMeta = PHASE_OF_PLAY_META[profile.focus];
  const sessions = useMemo(
    () => (isStageComplete('team', profile) ? buildOnboardingSessions(profile) : []),
    [profile],
  );
  // Track which side-panel fields were just learned, so CSS can flash them
  // to give the coach immediate evidence Brain populated something. Keyed
  // by a flash-id so re-flashing the same field replays the animation.
  const [flashing, setFlashing] = useState<Record<string, string>>({});
  const flashFields = useCallback((fields: string[]) => {
    if (fields.length === 0) return;
    const id = crypto.randomUUID();
    setFlashing((prev) => {
      const next = { ...prev };
      for (const f of fields) next[f] = id;
      return next;
    });
    window.setTimeout(() => {
      setFlashing((prev) => {
        const next = { ...prev };
        for (const f of fields) if (next[f] === id) delete next[f];
        return next;
      });
    }, 1800);
  }, []);

  // Voice loop — auto-speak every Brain reply when voice output is enabled
  // (regardless of whether the prior coach turn was typed or spoken). The
  // founder-sync 2026-04-30 hard rule: voice OUTPUT is core, not just input.
  // A header mute toggle gives instant silence when the coach needs it.
  const { prefs: voicePrefs, save: saveVoicePrefs } = useVoicePrefs();
  const tts = useTextToSpeech({
    provider: voicePrefs.voice_provider,
    rate: voicePrefs.voice_rate,
    voiceId: voicePrefs.voice_id,
  });
  const spokenTurnIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (extracting) return;
    const last = turns[turns.length - 1];
    if (!last || last.role !== 'brain') return;
    if (spokenTurnIdRef.current === last.id) return;
    spokenTurnIdRef.current = last.id;
    if (!voicePrefs.voice_enabled) return;
    if (last.text) tts.speakSentenceChunked(last.text);
  }, [turns, extracting, voicePrefs.voice_enabled, tts]);

  // Track the most recent Brain message so the extractor can bridge bare
  // affirmatives ("Yes" after Brain offers to seed the roster) — stateless
  // extraction would otherwise drop the consent. Updated via effect so we
  // don't pay a re-render cost on submit.
  const lastBrainReplyRef = useRef<string | null>(null);
  useEffect(() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      const turn = turns[i];
      if (turn.role === 'brain' && turn.text) {
        lastBrainReplyRef.current = turn.text;
        return;
      }
    }
  }, [turns]);

  // Mirror tts.isSpeaking into a ref so navigation handlers can poll the
  // live value without depending on a re-render. Used by
  // navigateToCalendarRespectingTts to defer a route change until the
  // current sentence has finished playing.
  const ttsSpeakingRef = useRef(false);
  useEffect(() => { ttsSpeakingRef.current = tts.isSpeaking; }, [tts.isSpeaking]);

  /** Navigate to /calendar but wait up to ~4s for TTS to drain so Brain's
   *  current sentence isn't cut off mid-word. After 4s, force-stop and
   *  navigate anyway (better to land in the app than to hang). */
  const navigateToCalendarRespectingTts = useCallback(async () => {
    const deadline = Date.now() + 4000;
    while (ttsSpeakingRef.current && Date.now() < deadline) {
      await new Promise((r) => window.setTimeout(r, 100));
    }
    tts.stop();
    navigate('/calendar');
  }, [tts, navigate]);

  // Walkthrough narration effect — fires when the coach enters the
  // walkthrough stage and on every walkthroughIndex advance. Brain adds
  // a short turn describing the current session; the existing TTS effect
  // (auto-speak last Brain turn) picks it up. Idempotency: we only inject
  // a turn we haven't injected yet — tracked via narratedIndexRef.
  const narratedIndexRef = useRef<number>(-1);
  useEffect(() => {
    if (stage !== 'walkthrough') {
      narratedIndexRef.current = -1;
      return;
    }
    if (sessions.length === 0) return;
    const i = Math.min(walkthroughIndex, sessions.length);
    if (narratedIndexRef.current === i) return;
    narratedIndexRef.current = i;
    let text: string;
    if (i >= sessions.length) {
      text = `That's Week 1 done. The full block continues from there — open the calendar whenever you're ready.`;
    } else {
      const session = sessions[i];
      const ordinal = i === 0 ? 'Session one' : i === 1 ? 'Session two' : i === 2 ? 'Session three' : `Session ${i + 1}`;
      const obj = session.objectives[0] ?? 'general session work';
      const intent = session.psychSocialIntent
        ? ` Mental-social intent: ${session.psychSocialIntent.toLowerCase().replace(/\.$/, '')}.`
        : '';
      const lead = i === 0
        ? `Right — let's walk through Week 1. ${ordinal} on ${session.date}: ${session.title}.`
        : `${ordinal}, ${session.date}: ${session.title}.`;
      const tail = i === sessions.length - 1
        ? ' Say "open calendar" when you want to dive in, or "next" to see one more.'
        : ' Say "next" for the next one, or "open calendar" any time.';
      text = `${lead} ${obj}.${intent}${tail}`;
    }
    setTurns((existing) => [
      ...existing,
      { id: crypto.randomUUID(), role: 'brain', text },
    ]);
  }, [stage, walkthroughIndex, sessions]);

  // Autoscroll the chat thread to the latest turn (mirrors BrainChatPanel).
  const threadRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!threadRef.current) return;
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [turns.length, extracting]);

  // Derive Philosophy + Curriculum at the right moment in the flow:
  //   - Academy coaches: when the stage advances to 'philosophy'
  //   - Grassroots / NOVICE coaches: when the stage advances to 'confirm'
  //     (silent in-background derive — referenced in the confirm summary)
  // The deriving ref guards against re-entry if the effect fires twice from
  // React strict mode or rapid state changes.
  const derivingRef = useRef(false);
  useEffect(() => {
    if (derivingRef.current) return;
    if (profile.philosophy || profile.curriculum) return;
    const shouldDerive =
      stage === 'philosophy' ||
      (stage === 'confirm' && isImplicitPhilosophyCoach(profile)) ||
      stage === 'curriculum';
    if (!shouldDerive) return;
    derivingRef.current = true;
    const academyPath = !isImplicitPhilosophyCoach(profile);
    if (academyPath) {
      setExtracting(true);
    }
    void (async () => {
      try {
        const { philosophy, curriculum } = await derivePhilosophyAndCurriculum(
          profile,
          coachFirstName,
          user?.id ?? null,
        );
        setProfile((p) => ({ ...p, philosophy, curriculum }));
        flashFields(['philosophy', 'curriculum']);
        if (academyPath) {
          // Academy coach is on the philosophy stage waiting for the draft.
          // Surface Brain's stage question now that we have something to show.
          setTurns((existing) => [
            ...existing,
            {
              id: crypto.randomUUID(),
              role: 'brain',
              text: stageQuestion('philosophy', { ...profile, philosophy, curriculum }),
            },
          ]);
        }
      } finally {
        derivingRef.current = false;
        if (academyPath) setExtracting(false);
      }
    })();
  }, [stage, profile, coachFirstName, flashFields]);

  // Unused since the voice picker took over mute responsibility, but kept
  // in case a future surface wants a one-tap mute toggle.
  void saveVoicePrefs;
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const voiceTriggerRef = useRef<HTMLButtonElement>(null);

  const save = useCallback(async () => {
    if (saving) return;
    if (!user) {
      setSaveMessage('Sign in before saving onboarding.');
      return;
    }
    setSaving(true);
    setSaveMessage('Building Week 1...');
    let sessionCount = sessions.length || 3;
    let localOnly = false;
    let warnings: string[] = [];
    try {
      const result = await saveOnboardingPlan(user.id, profile);
      sessionCount = result.sessionCount;
      warnings = result.warnings;
      saveLocalTeam(profile, sessions);
      clearOnboardingDeferred();
      setCoachLevel(profile.coachLevel);
    } catch (err) {
      // Supabase unreachable (placeholder env etc) — fall back to localStorage.
      localOnly = true;
      saveLocalTeam(profile, sessions);
      clearOnboardingDeferred();
      setCoachLevel(profile.coachLevel);
      const reason = err instanceof Error ? err.message : String(err);
      warnings = [reason];
    }
    setSaved(true);
    const warningText = warnings.length > 0 ? ` ${warnings.join(' ')}` : '';
    setSaveMessage(
      localOnly
        ? `Saved locally — will sync when online.${warningText}`
        : `Calendar populated with ${sessionCount} sessions.${warningText}`,
    );

    // Compose the Brain success reply via Sonnet so the voice stays warm
    // and it always offers concrete next steps.
    const replyText = await composeSaveSuccessReply({
      profile,
      sessionCount,
      coachFirstName,
      coachId: user?.id ?? null,
      localOnly,
    });
    setTurns((existing) => [
      ...existing,
      { id: crypto.randomUUID(), role: 'brain', text: replyText },
    ]);
    setSaving(false);
  }, [profile, saving, sessions, setCoachLevel, user, coachFirstName]);

  // ---------------------------------------------------------------------
  // Stage-driven submit:
  //   1. confirmation at 'confirm' stage → save
  //   2. fuzzy edit intent → jump back to the relevant stage
  //   3. skip → fill defaults for the current stage + advance
  //   4. otherwise → extract, update, advance if stage complete
  // ---------------------------------------------------------------------
  const submit = useCallback(async (text: string) => {
    const clean = text.trim();
    if (!clean) return;

    // Onboarding is complete — chat is locked. Any further input is
    // ignored so Brain doesn't keep repeating the setup summary while the
    // user is trying to get into the actual app. The CTA buttons + auto-
    // navigation handle the next step.
    if (stage === 'done') return;

    setTurns((existing) => [
      ...existing,
      { id: crypto.randomUUID(), role: 'coach', text: clean },
    ]);

    // Walkthrough-stage intents — "next" / "open calendar" / "skip" —
    // handled inline without an AI round-trip. Keeps the walkthrough
    // tight: short coach utterance, immediate action.
    if (stage === 'walkthrough') {
      const lower = clean.toLowerCase();
      if (/\b(open(\s+the)?\s+calendar|i'?ll\s+have\s+a\s+look|let\s+me\s+see|let\s+me\s+have\s+a\s+look|i'?m\s+good|all\s+done|done|finish|exit|i'?ll\s+take\s+it\s+from\s+here)\b/.test(lower)) {
        await navigateToCalendarRespectingTts();
        return;
      }
      if (/\b(next|next\s+session|another|go\s+on|continue|move\s+on|carry\s+on)\b/.test(lower)) {
        setWalkthroughIndex((i) => i + 1);
        return;
      }
      // Affirmatives ("yes", "walk me through it") start narration if we
      // haven't auto-fired yet, or simply advance.
      if (/^(yes|yeah|please|go|walk|talk)\b/.test(lower)) {
        setWalkthroughIndex((i) => (i === 0 ? 0 : i + 1));
        // The narration effect picks this up via walkthroughIndex change OR
        // because the initial 0-index narration was idempotently injected.
        return;
      }
      // Otherwise a free-form question — keep it simple for now: gentle nudge.
      setTurns((existing) => [
        ...existing,
        {
          id: crypto.randomUUID(),
          role: 'brain',
          text: `Say "next" for the next session, or "open calendar" when you're ready to dive in.`,
        },
      ]);
      return;
    }

    // (1) Confirmation at the confirm stage → trigger save → walkthrough.
    //     We DON'T auto-navigate to /calendar any more. The walkthrough
    //     stage stays on the Onboarding surface so Brain can read Week 1
    //     session-by-session (or the coach can open the calendar themselves
    //     via the explicit button, which waits for TTS to flush — see
    //     navigateToCalendarRespectingTts).
    if (stage === 'confirm' && isOnboardingConfirmation(clean) && !saved && !saving) {
      await save();
      setStage('walkthrough');
      return;
    }

    // (2) Fuzzy edit intent — coach wants to revise an earlier field. Jump
    // back to that stage, run extraction on this utterance so the new value
    // sticks, then leave the user there. They can correct → Brain advances
    // back through the remaining stages.
    const edit = detectEditIntent(clean);
    if (edit && edit.stage !== stage) {
      setExtracting(true);
      try {
        const result = await extractOnboardingInput(profile, clean, {
          stage: edit.stage,
          coachFirstName,
          coachId: user?.id ?? null,
          lastBrainReply: lastBrainReplyRef.current,
        });
        const captured = softCaptureForStage(edit.stage, result.profile, clean);
        const heard = diffProfileForChips(profile, captured);
        setProfile(captured);
        setStage(edit.stage);
        setSaved(false);
        setSaveMessage(null);
        setTurns((existing) => [
          ...existing,
          {
            id: crypto.randomUUID(),
            role: 'brain',
            text: result.reply
              ? result.reply
              : `Switched back to ${STAGE_META[edit.stage].label.toLowerCase()}. ${stageQuestion(edit.stage, captured)}`,
            heard: heard.length > 0 ? heard : undefined,
          },
        ]);
        flashFields(heard.map((c) => c.field));
      } finally {
        setExtracting(false);
      }
      return;
    }

    // (3a) Test-seed shortcut at the roster stage — devs / demoers can
    // populate the roster with realistic UK U11 names instead of
    // "Player 1, Player 2..." placeholders. Triggered by saying any of
    // "seed test players", "use test roster", "fake roster", "seed real
    // names". Only active at the roster stage.
    if (stage === 'roster' && /^(?:seed\s+test|use\s+test\s+roster|fake\s+roster|seed\s+real\s+names|test\s+seed)\b/i.test(clean)) {
      const seeded = applySeededTestRoster(profile);
      const heard = diffProfileForChips(profile, seeded);
      let cursor: OnboardingStage = stage;
      while (cursor !== 'confirm' && cursor !== 'done' && isStageComplete(cursor, seeded)) {
        const candidate = nextStage(cursor, seeded);
        cursor = candidate;
        if (candidate === 'philosophy' || candidate === 'curriculum') break;
      }
      const newStage = cursor;
      const advanced = newStage !== stage;
      setProfile(seeded);
      if (advanced) setStage(newStage);
      setTurns((existing) => [
        ...existing,
        {
          id: crypto.randomUUID(),
          role: 'brain',
          text: `Seeded ${seeded.roster?.length ?? 0} test players. ${advanced ? stageQuestion(newStage, seeded) : ''}`,
          heard: heard.length > 0 ? heard : undefined,
        },
      ]);
      flashFields(heard.map((c) => c.field));
      return;
    }

    // (3) Skip — fill defaults for this stage and advance.
    if (isSkipRequest(clean)) {
      const filled = fillStageDefaults(stage, profile);
      const heard = diffProfileForChips(profile, filled);
      let cursor: OnboardingStage = stage;
      while (cursor !== 'confirm' && cursor !== 'done' && isStageComplete(cursor, filled)) {
        const candidate = nextStage(cursor, filled);
        cursor = candidate;
        // Pause at review stages so the coach actually sees the draft.
        if (candidate === 'philosophy' || candidate === 'curriculum') break;
      }
      const newStage = cursor;
      const advanced = newStage !== stage;
      setProfile(filled);
      if (advanced) setStage(newStage);
      setTurns((existing) => [
        ...existing,
        {
          id: crypto.randomUUID(),
          role: 'brain',
          text: advanced ? stageQuestion(newStage, filled) : stageAcknowledgement(stage, filled, false),
          heard: heard.length > 0 ? heard : undefined,
        },
      ]);
      flashFields(heard.map((c) => c.field));
      return;
    }

    // (4) Normal extraction.
    setExtracting(true);
    try {
      const result = await extractOnboardingInput(profile, clean, {
        stage,
        coachFirstName,
        coachId: user?.id ?? null,
        lastBrainReply: lastBrainReplyRef.current,
      });
      // Soft capture — when AI + regex leave the current stage incomplete
      // but the coach clearly answered (e.g. "team organization" at the aims
      // stage), stamp the raw utterance into the stage's primary free-text
      // field. Stops Brain from re-asking after a real reply.
      const captured = softCaptureForStage(stage, result.profile, clean);
      const heard = diffProfileForChips(profile, captured);

      // Cascade — a single utterance may fill multiple consecutive stages.
      // Advance through any complete stages until we hit confirm or an
      // incomplete one. (Confirm itself only advances via "yes".) Pauses
      // at review stages (philosophy/curriculum) so academy coaches see
      // each draft before moving on.
      let cursor: OnboardingStage = stage;
      while (cursor !== 'confirm' && cursor !== 'done' && isStageComplete(cursor, captured)) {
        const candidate = nextStage(cursor, captured);
        cursor = candidate;
        if (candidate === 'philosophy' || candidate === 'curriculum') break;
      }
      const newStage = cursor;
      const advanced = newStage !== stage;

      setProfile(captured);
      if (advanced) setStage(newStage);
      setTurns((existing) => [
        ...existing,
        {
          id: crypto.randomUUID(),
          role: 'brain',
          // Prefer Sonnet's composed reply when available — it's the
          // personality voice. Fall back to scripted ack if AI failed.
          text: result.reply
            ? result.reply
            : advanced
              ? stageQuestion(newStage, captured)
              : stageAcknowledgement(stage, captured, false),
          heard: heard.length > 0 ? heard : undefined,
        },
      ]);
      flashFields(heard.map((c) => c.field));
    } finally {
      setExtracting(false);
    }
  }, [stage, profile, saved, saving, save, flashFields, coachFirstName, navigateToCalendarRespectingTts, user]);

  const voice = useVoiceToText({
    onTranscript: (t) => {
      void submit(t);
    },
  });

  const skip = useCallback(() => {
    markOnboardingDeferred();
    navigate('/');
  }, [navigate]);

  return (
    <main className="onboarding" aria-label="Coach onboarding">
      <header className="onboarding__top">
        <Brand team={profile.teamName ?? 'Layer 1 onboarding'} />
        <div className="onboarding__top-actions">
          <GlassPill mono>Layer 1 · under 10 min</GlassPill>
          <Button
            type="button"
            variant="glass"
            size="workbench"
            className="onboarding__skip"
            onClick={skip}
            aria-label="Skip onboarding and go to the app"
          >
            Skip → Today
          </Button>
        </div>
      </header>

      <section className="onboarding__split">
        {/* LEFT — generated content / artefact. The thing Brain is populating. */}
        <section className="onboarding__artefact" aria-label="Team being learned by Brain">
          <header className="onboarding__artefact-head">
            <span className="onboarding__eyebrow">Building your team</span>
            <span className="onboarding__count">{stagesDone}/{ONBOARDING_STAGE_ORDER.length}</span>
          </header>
          <h1 className="onboarding__artefact-title">
            {stagesDone === 0
              ? 'Talk to Brain. Your team appears here.'
              : stage === 'done'
                ? 'All set.'
                : 'Your team, learned by Brain.'}
          </h1>

          <ol className="onboarding__stages">
            {ONBOARDING_STAGE_ORDER.map((s) => {
              const meta = STAGE_META[s];
              const done = isStageComplete(s, profile);
              const active = s === stage;
              const state = active ? 'active' : done ? 'done' : 'pending';
              const flashKey = STAGE_FLASH_KEYS[s].find((k) => flashing[k]);
              return (
                <li
                  key={s}
                  className="onboarding__stage"
                  data-state={state}
                  data-flash={flashKey ? flashing[flashKey] : undefined}
                  aria-current={active ? 'step' : undefined}
                >
                  <header className="onboarding__stage-head">
                    <span className="onboarding__stage-tag">
                      {done ? '✓' : active ? '●' : '○'}
                    </span>
                    <span className="onboarding__stage-label">{meta.label}</span>
                    <span className="onboarding__stage-desc">{meta.description}</span>
                  </header>
                  <StageBody stage={s} profile={profile} phaseLabel={phaseMeta.label} />
                </li>
              );
            })}
          </ol>

          {stagesDone >= 1 && (
            <div className="onboarding__edit" style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {stage !== 'confirm' && (
                <button
                  type="button"
                  className="onboarding__edit-toggle"
                  onClick={() => setEditOpen((o) => !o)}
                  aria-expanded={editOpen}
                  style={{
                    alignSelf: 'flex-start',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.16)',
                    borderRadius: 8,
                    color: 'inherit',
                    font: 'inherit',
                    fontSize: 12,
                    padding: '6px 12px',
                    cursor: 'pointer',
                  }}
                >
                  {editOpen ? 'Hide details' : '✎ Edit details'}
                </button>
              )}
              {(editOpen || stage === 'confirm') && (
                <TeamSetupForm
                  values={profile}
                  onChange={(patch) => setProfile((p) => ({ ...p, ...patch }))}
                />
              )}
            </div>
          )}

          {sessions.length > 0 && (
            <div className="onboarding__week" data-flash={flashing.week || undefined}>
              <span className="onboarding__eyebrow">Week 1 draft preview</span>
              {sessions.map((session) => (
                <article key={session.date} className="onboarding__session">
                  <time>{session.date}</time>
                  <strong>{session.title}</strong>
                  <p>{session.objectives[0]}</p>
                </article>
              ))}
            </div>
          )}

          <div className="onboarding__actions">
            {!saved ? (
              <Button
                variant="primary"
                size="touchline"
                onClick={() => void save()}
                disabled={stage !== 'confirm'}
              >
                {saving ? 'Building Week 1…' : stage === 'confirm' ? 'Confirm & populate calendar' : 'Keep talking with Brain…'}
              </Button>
            ) : (
              <>
                <Button variant="primary" size="touchline" onClick={() => navigate('/season')}>
                  Plan my season
                </Button>
                <Button variant="glass" size="touchline" onClick={() => void navigateToCalendarRespectingTts()}>
                  Open calendar
                </Button>
                <Button variant="glass" size="touchline" onClick={() => navigate('/')}>
                  Open Home
                </Button>
              </>
            )}
          </div>
          {saveMessage && <p className="onboarding__save" data-saved={saved || undefined}>{saveMessage}</p>}
        </section>

        {/* RIGHT — Brain. Compact hero + chat + prompt anchored at the bottom of the column. */}
        <aside className="onboarding__brain" aria-label="Brain first run conversation">
          <header className="onboarding__brain-head">
            <BrainOrb
              size="md"
              state={voice.listening ? 'listening' : extracting ? 'thinking' : 'idle'}
              ariaLabel="Brain"
            />
            <div className="onboarding__brain-titles">
              <span className="onboarding__eyebrow">Brain first run</span>
              <h2>Set up the season by talking.</h2>
            </div>
            <div className="onboarding__voice-wrap">
              <button
                ref={voiceTriggerRef}
                type="button"
                className="onboarding__mute"
                onClick={() => setVoicePickerOpen((open) => !open)}
                aria-label="Voice settings"
                aria-pressed={voicePickerOpen}
                aria-expanded={voicePickerOpen}
                title={
                  voicePrefs.voice_enabled
                    ? `Voice: ${voicePrefs.voice_provider === 'cloud' ? `cloud · ${voicePrefs.voice_id ?? 'fable'}` : 'system'}`
                    : 'Voice muted — click to change'
                }
              >
                {voicePrefs.voice_enabled ? '🔊' : '🔇'}
              </button>
              {voicePickerOpen && (
                <VoicePicker
                  onClose={() => setVoicePickerOpen(false)}
                  anchor={voiceTriggerRef.current}
                />
              )}
            </div>
          </header>

          <div className="onboarding__thread" ref={threadRef} aria-live="polite">
            {turns.map((turn) => (
              <div
                key={turn.id}
                className={`onboarding__turn onboarding__turn--${turn.role}`}
              >
                <span className="onboarding__turn-speaker">
                  {turn.role === 'brain' && <BrainOrb size="xs" ariaLabel="Brain" />}
                  {turn.role === 'brain' ? 'Brain' : 'Coach'}
                </span>
                {turn.heard && turn.heard.length > 0 && (
                  <div className="onboarding__heard" aria-label="What Brain heard">
                    <span className="onboarding__heard-label">Heard</span>
                    {turn.heard.map((chip) => (
                      <span key={chip.field} className="onboarding__heard-chip">
                        <span className="onboarding__heard-field">{chip.field}</span>
                        <span className="onboarding__heard-value">{chip.value}</span>
                      </span>
                    ))}
                  </div>
                )}
                <p>{turn.text}</p>
              </div>
            ))}
            {extracting && (
              <div className="onboarding__turn onboarding__turn--brain onboarding__turn--pending">
                <span className="onboarding__turn-speaker">
                  <BrainOrb size="xs" state="thinking" ariaLabel="Brain" />
                  Brain
                </span>
                <p className="onboarding__typing">
                  <span /><span /><span />
                </p>
              </div>
            )}
          </div>

          {stage === 'walkthrough' ? (
            <WalkthroughPanel
              sessions={sessions}
              index={walkthroughIndex}
              onNext={() => setWalkthroughIndex((i) => i + 1)}
              onOpenCalendar={() => void navigateToCalendarRespectingTts()}
            />
          ) : stage === 'done' ? (
            <div className="onboarding__done-banner" role="status">
              <span className="onboarding__done-eyebrow">Setup complete</span>
              <p>Next: map your season — Brain drafts a multi-week theme plan you review and tweak. Or jump straight to the calendar.</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button variant="primary" size="touchline" onClick={() => navigate('/season')}>
                  Plan my season
                </Button>
                <Button variant="glass" size="touchline" onClick={() => void navigateToCalendarRespectingTts()}>
                  Open calendar
                </Button>
              </div>
            </div>
          ) : (
            <BrainPrompt
              hint="click the orb to talk, or type below"
              placeholder="I coach U9, 12 players, mostly new, and we need help with defending..."
              listening={voice.listening}
              voiceStatus={voice.status}
              interim={voice.interim}
              voicePhase={voice.phase}
              micPermission={voice.permission}
              micMode="toggle"
              onMicStart={() => void voice.start()}
              onMicStop={voice.stop}
              onSubmit={submit}
            />
          )}

          {!getFeatures().under13PlayerAccounts && (
            <details className="onboarding__v1-scope-fold">
              <summary>Note about player accounts (v1)</summary>
              <p className="onboarding__v1-scope">
                4CM works for teams of any age. Your U7–U12 players are
                fully supported — you can plan their sessions, rate their
                development across the four corners, and share progress
                with their parents. Players aged 13 and above can claim
                their own login to self-rate, keep a development journal,
                and interact with the Brain assistant. We&apos;ll open up
                player accounts for under-13s in a future release.
              </p>
            </details>
          )}
        </aside>
      </section>
    </main>
  );
}
